import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, Clock, Map, Ticket, CreditCard, Navigation, Lightbulb, Timer, IndianRupee, Smile, Leaf, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { EarlyAccessModal } from '../components/EarlyAccessModal';
import { features, benefits, partners, testimonials } from '../data/mock';

const iconMap = {
  Clock, Map, Ticket, CreditCard, Navigation, Lightbulb,
  Timer, IndianRupee, Smile, Leaf
};

export const Home = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <EarlyAccessModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-[#1B4F72] via-[#2E86AB] to-[#138808]">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FF9933] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block">
                <span className="backdrop-blur-sm !font-medium !text-sm px-4 py-2 rounded-full text-white bg-white/20">🇮🇳 India's Unified Mobility Layer 

                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight">
                One Platform.
                <span className="block mt-2 text-[#FF9933]">All Mobility</span>
              </h1>
              <p className="text-xl text-gray-100 leading-relaxed">
                Connecting India's fragmented transport systems into one interoperable, real-time platform. 
                From buses to metros, trains to cabs - seamless mobility for everyone.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-[#FF9933] hover:bg-[#E68A2E] text-white font-semibold px-8 py-6 rounded-lg shadow-xl transition-all duration-300 hover:scale-105 text-lg group"
                >
                  Get Early Access
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-[#1B4F72] font-semibold px-8 py-6 rounded-lg transition-all duration-300 text-lg group"
                >
                  <Play className="mr-2 group-hover:scale-110 transition-transform" size={20} />
                  Watch Demo
                </Button>
              </div>
              {/* <div className="flex items-center space-x-8 pt-4">
                <div>
                  <p className="text-3xl font-bold text-white">50K+</p>
                  <p className="text-gray-200 text-sm">Active Users</p>
                </div>
                <div className="h-12 w-px bg-white/30"></div>
                <div>
                  <p className="text-3xl font-bold text-white">8+</p>
                  <p className="text-gray-200 text-sm">Transport Partners</p>
                </div>
                <div className="h-12 w-px bg-white/30"></div>
                <div>
                  <p className="text-3xl font-bold text-white">4.8</p>
                  <p className="text-gray-200 text-sm">User Rating</p>
                </div>
              </div> */}
            </div>

            {/* Right Content - Logo */}
            <div className="relative">
              <div className="relative w-full max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-r from-[#FF9933] to-[#138808] rounded-full blur-3xl opacity-30 animate-pulse"></div>
                <img
                  src = "assets/logos/mahasarathi-logo.jpg"
                  // src="https://customer-assets.emergentagent.com/job_commute-ease/artifacts/xmbb6txn_PixVerse_Image_Effect_prompt_animate%20this%20logo.jpg"
                  alt="Mahasarathi Platform"
                  className="relative z-10 w-full rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#FF9933] font-semibold text-sm uppercase tracking-wider">Features</span>
            <h2 className="text-4xl font-bold text-[#1B4F72] mt-3 mb-4">How Mahasarathi Works</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Seamless integration of multiple transport modes for your convenience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card
                  key={feature.id}
                  className="group hover:shadow-xl transition-all duration-300 border-0 bg-white hover:-translate-y-2"
                  style={{ animationDelay: `${index * 100}ms` }}>

                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#FF9933] to-[#E68A2E] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-[#1B4F72] mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>);

            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#FF9933] font-semibold text-sm uppercase tracking-wider">Benefits</span>
            <h2 className="text-4xl font-bold text-[#1B4F72] mt-3 mb-4">Why Choose Mahasarathi?</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Experience the difference with our integrated mobility solution
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit) => {
              const Icon = iconMap[benefit.icon];
              return (
                <div
                  key={benefit.id}
                  className="text-center group hover:transform hover:scale-105 transition-all duration-300">

                  <div className="w-20 h-20 bg-gradient-to-br from-[#138808] to-[#0F6806] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-[#1B4F72] mb-3">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>);

            })}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#FF9933] font-semibold text-sm uppercase tracking-wider">Stakeholders</span>
            <h2 className="text-4xl font-bold text-[#1B4F72] mt-3 mb-4">Who We Serve</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Creating value for every participant in India's mobility ecosystem
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#FF9933] to-[#E68A2E] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Smile className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">Commuters</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Multi-modal planning, unified payments, and real-time intelligence for hassle-free travel.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Live ETAs across all modes
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    Seamless journey planning
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#FF9933] rounded-full mr-2 mt-2"></span>
                    One wallet for all transport
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1B4F72] to-[#2E86AB] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Navigation className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">Governments & Cities</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Unified, real-time visibility and data-driven insights without vendor lock-in.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Real-time dashboards
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Policy planning tools
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#1B4F72] rounded-full mr-2 mt-2"></span>
                    Open, neutral platform
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#138808] to-[#0F6806] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <Ticket className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#1B4F72] mb-4">Transport Operators</h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Digitization tools, optimization insights, and increased utilization without middleman fees.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Fleet optimization
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Revenue analytics
                  </li>
                  <li className="flex items-start">
                    <span className="w-1.5 h-1.5 bg-[#138808] rounded-full mr-2 mt-2"></span>
                    Full operator control
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#FF9933] font-semibold text-sm uppercase tracking-wider">Partners</span>
            <h2 className="text-4xl font-bold text-[#1B4F72] mt-3 mb-4">Trusted Integrations</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Partnering with India's leading transport providers
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {partners.map((partner) =>
            <div
              key={partner.id}
              className="bg-white rounded-xl p-8 flex flex-col items-center justify-center hover:shadow-lg transition-all duration-300 group border border-gray-100">

                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">
                  {partner.category === 'Train' && '🚂'}
                  {partner.category === 'Bus' && '🚌'}
                  {partner.category === 'Metro' && '🚇'}
                  {partner.category === 'Cab' && '🚕'}
                  {partner.category === 'Auto' && '🛺'}
                  {partner.category === 'Flight' && '✈️'}
                  {partner.category === 'Integration' && '🔗'}
                </div>
                <h3 className="text-lg font-semibold text-[#1B4F72] text-center">{partner.name}</h3>
                <span className="text-sm text-gray-500 mt-1">{partner.category}</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-[#FF9933] font-semibold text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="text-4xl font-bold text-[#1B4F72] mt-3 mb-4">What Our Users Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) =>
            <Card key={testimonial.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) =>
                  <Star key={i} className="text-[#FF9933] fill-current" size={18} />
                  )}
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-6 italic">"{testimonial.content}"</p>
                  <div>
                    <p className="font-semibold text-[#1B4F72]">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#1B4F72] to-[#138808]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Commute?
          </h2>
          <p className="text-xl text-gray-100 mb-8 leading-relaxed">
            Join thousands of happy commuters who have simplified their travel with Mahasarathi
          </p>
          {/* <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-[#FF9933] hover:bg-[#E68A2E] text-white font-semibold px-10 py-6 rounded-lg shadow-xl transition-all duration-300 hover:scale-105 text-lg"
            >
              Start Free Trial
            </Button> */}
            {/* <Link to="/contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1B4F72] font-semibold px-10 py-6 rounded-lg transition-all duration-300 text-lg">
                Contact Sales
              </Button>
            </Link>
          </div> */}
        </div>
      </section>
    </div>);

};